import { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Radio, 
  Button, 
  Chip,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { Edit, AutoAwesome } from '@mui/icons-material';
import Sidebar from '../components/dashboard/Sidebar';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import ManualQuestionForm from '../components/survey/ManualQuestionForm';
import AIQuestionGenerator from '../components/survey/AIQuestionGenerator';
import QuestionList from '../components/survey/QuestionList';
import SurveyPreviewQuestions from '../components/survey/SurveyPreviewQuestions';
import { getFullUserData, getUserBusinessName } from '../utils/userHelper';
import { SurveyQuestion, QuestionFormData, CreationMethod } from '../types/survey';
import { createSurveyFromForm } from '../services/surveyService';
import { API_BASE_URL, API_ENDPOINTS } from '../config/api';
import { api } from '../utils/apiClient';

const PageContainer = styled(Box)({
  display: 'flex',
  minHeight: '100vh',
  bgcolor: '#F9FAFB'
});

const MainContent = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column'
});

const ContentArea = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  maxWidth: '1400px',
  margin: '0 auto',
  width: '100%',
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: theme.spacing(3),
  [theme.breakpoints.up('lg')]: {
    gridTemplateColumns: '1fr 400px'
  }
}));

const StyledCard = styled(Card)({
  backgroundColor: 'white',
  border: '1px solid #E5E7EB',
  borderRadius: '12px',
  marginBottom: '24px',
  boxShadow: 'none'
});

const MethodCard = styled(Box)<{ selected: boolean }>(({ selected }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '20px',
  padding: '24px',
  border: selected ? '2px solid #4F46E5' : '2px solid #E5E7EB',
  borderRadius: '12px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  backgroundColor: selected ? '#EEF2FF' : 'white',
  '&:hover': {
    borderColor: selected ? '#4F46E5' : '#D1D5DB',
    backgroundColor: selected ? '#EEF2FF' : '#F9FAFB'
  }
}));

const IconContainer = styled(Box)<{ bgColor: string }>(({ bgColor }) => ({
  width: 56,
  height: 56,
  borderRadius: '12px',
  backgroundColor: bgColor,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0
}));

const CreateSurvey = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userData = getFullUserData();
  const businessName = getUserBusinessName();

  // Get campaign data from location state if available
  const campaignData = location.state?.campaignData;
  const campaignId = location.state?.campaignId;
  const isEditMode = location.state?.isEditMode || false;

  const [surveyMethod, setSurveyMethod] = useState<'manual' | 'ai' | null>(null);
  const [questions, setQuestions] = useState<SurveyQuestion[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editingQuestion, setEditingQuestion] = useState<SurveyQuestion | null>(null);
  const [editForm, setEditForm] = useState<{
    question: string;
    answerType: string;
    isMandatory: boolean;
  }>({
    question: '',
    answerType: 'text',
    isMandatory: false
  });

  // Load existing survey questions when editing a draft campaign
  useEffect(() => {
    if (campaignData?.survey?.questions && campaignData.survey.questions.length > 0) {
      console.log('📋 Loading existing survey questions:', campaignData.survey.questions);
      
      // Map API format to UI format
      const mapAnswerType = (apiType: string): string => {
        const typeMap: Record<string, string> = {
          'text': 'text',
          'rating': 'rating',
          'number': 'number',
          'yes_no': 'yesno',
          'yesno': 'yesno',
          'multiple_choice': 'multiple_choice',
          'multiple choice': 'multiple_choice',
        };
        
        const normalizedType = apiType?.toLowerCase().trim() || 'text';
        return typeMap[normalizedType] || 'text';
      };

      const existingQuestions: SurveyQuestion[] = campaignData.survey.questions
        .sort((a, b) => (a.question_order || 0) - (b.question_order || 0))
        .map((q: any, index: number) => {
          const mappedAnswerType = mapAnswerType(q.answer_type || q.answerType || 'text');
          return {
            id: q.id || `question-${Date.now()}-${index}`,
            question: q.question_text || q.question || '', // UI format
            question_text: q.question_text || q.question || '', // API format
            answerType: mappedAnswerType, // UI format
            answer_type: q.answer_type || q.answerType || 'text', // API format
            isMandatory: q.is_mandatory !== undefined ? q.is_mandatory : (q.isMandatory !== undefined ? q.isMandatory : false), // UI format
            is_mandatory: q.is_mandatory !== undefined ? q.is_mandatory : (q.isMandatory !== undefined ? q.isMandatory : false), // API format
            question_order: q.question_order !== undefined ? q.question_order : index + 1,
            options: q.options || undefined,
          };
        });

      console.log('✅ Mapped questions to UI format:', existingQuestions);
      setQuestions(existingQuestions);
      
      // Set method to manual if questions exist (allows editing and adding more)
    setSurveyMethod('manual');
    }
  }, [campaignData]);

  const handleMethodSelect = (method: 'manual' | 'ai') => {
    setSurveyMethod(method);
  };

  const handleAddQuestion = (questionData: QuestionFormData) => {
    const newQuestion: SurveyQuestion = {
      id: `question-${Date.now()}`,
      question: questionData.question,
      answerType: questionData.answerType,
      isMandatory: questionData.isMandatory,
      placeholder: questionData.answerType === 'number' ? 'Enter a number from 1-10' : undefined
    };

    console.log('Adding question with mandatory:', questionData.isMandatory, 'Type:', typeof questionData.isMandatory);
    setQuestions([...questions, newQuestion]);
  };

  const handleGenerateQuestions = async (productUrl: string) => {
    setIsGenerating(true);
    setError(null);
    console.log('Generating questions for:', productUrl);

    try {
      const url = `${API_BASE_URL.CAMPAIGN}${API_ENDPOINTS.SURVEY.GENERATE_QUESTIONS}`;
      
      const response = await api.post<{
        success: boolean;
        data?: {
          questions: Array<{
            question_text: string;
            suggested_answer_type: string;
          }>;
          total_generated?: number;
        };
        message?: string;
        error?: string;
      }>(url, {
        product_url: productUrl
      });

      console.log('AI Question Generation Response:', response);

      if (response.success && response.data?.questions) {
        const mapAnswerType = (suggestedType: string): string => {
          const typeMap: Record<string, string> = {
            'text': 'text',
            'rating': 'rating',
            'number': 'number',
            'yes_no': 'yesno',
            'yesno': 'yesno',
            'multiple_choice': 'multiple_choice',
            'multiple choice': 'multiple_choice',
          };
          
          const normalizedType = suggestedType.toLowerCase().trim();
          return typeMap[normalizedType] || 'text';
        };

        const generatedQuestions: SurveyQuestion[] = response.data.questions.map((q, index) => ({
          id: `question-${Date.now()}-${index + 1}`,
          question: q.question_text,
          answerType: mapAnswerType(q.suggested_answer_type),
          isMandatory: false,
        }));

        setQuestions(generatedQuestions);
        setSuccess(`Successfully generated ${response.data.total_generated || generatedQuestions.length} questions!`);
      } else {
        throw new Error(response.message || response.error || 'Failed to generate questions');
      }
    } catch (err) {
      console.error('Error generating questions:', err);
      const errorMessage = err instanceof Error 
        ? err.message 
        : 'Failed to generate questions. Please try again.';
      setError(errorMessage);
      setQuestions([]);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleEditQuestion = (question: SurveyQuestion) => {
    setEditingQuestion(question);
    setEditForm({
      question: question.question || '',
      answerType: question.answerType || 'text',
      isMandatory: question.isMandatory || false
    });
  };

  const handleSaveEdit = () => {
    if (!editingQuestion || !editForm.question.trim()) {
      return;
    }

    const updatedQuestions = questions.map(q => 
      q.id === editingQuestion.id
        ? {
            ...q,
            question: editForm.question.trim(),
            answerType: editForm.answerType,
            isMandatory: editForm.isMandatory
          }
        : q
    );

    setQuestions(updatedQuestions);
    setEditingQuestion(null);
    setEditForm({
      question: '',
      answerType: 'text',
      isMandatory: false
    });
  };

  const handleCancelEdit = () => {
    setEditingQuestion(null);
    setEditForm({
      question: '',
      answerType: 'text',
      isMandatory: false
    });
  };

  const handleDeleteQuestion = (questionId: string) => {
    setQuestions(questions.filter(q => q.id !== questionId));
  };

  const handleReorderQuestions = (reorderedQuestions: SurveyQuestion[]) => {
    setQuestions(reorderedQuestions);
  };

  const handleSaveAndContinue = async () => {
    setError(null);
    setSuccess(null);
    setIsLoading(true);

    try {
      if (!campaignId) {
        throw new Error('Campaign ID is required. Please create a campaign first.');
      }

      if (questions.length === 0) {
        throw new Error('Please add at least one question to the survey');
      }

      const apiQuestions = questions.map((q, index) => ({
        question_text: q.question_text || q.question || '',
        answer_type: q.answer_type || q.answerType || 'text',
        is_mandatory: q.is_mandatory !== undefined ? q.is_mandatory : Boolean(q.isMandatory),
        question_order: q.question_order !== undefined ? q.question_order : index + 1,
        ...(q.options && q.options.length > 0 ? { options: q.options } : {})
      }));

      console.log('Creating survey with questions:', JSON.stringify(apiQuestions, null, 2));

      const response = await createSurveyFromForm({
        campaignId: campaignId,
        surveyName: campaignData?.campaign_name || 'Customer Feedback Survey',
        description: `Survey created ${surveyMethod === 'ai' ? 'using AI' : 'manually'}`,
        creationMethod: surveyMethod === 'ai' ? CreationMethod.AI : CreationMethod.MANUAL,
        questions: apiQuestions as any,
      });

      console.log('Survey created successfully:', response);
      setSuccess('Survey created successfully! Redirecting...');

      navigate('/campaigns/create/customers', {
        state: { 
          campaignData, 
          surveyData: response.data?.survey,
          campaignId 
        }
      });

    } catch (err) {
      console.error('Survey creation error:', err);
      setError(err instanceof Error ? err.message : 'Failed to create survey. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageContainer>
      <Sidebar user={userData} />
      
      <MainContent>
        <DashboardHeader
          businessName={businessName}
          userAvatar={userData.avatar}
          businessLogo={userData.businessLogo}
        />
        
        <ContentArea>
          {/* Left Column */}
          <Box>
            {/* Page Heading */}
            <Box sx={{ mb: 4 }}>
              <Typography
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  fontWeight: 700,
                  fontSize: { xs: '1.875rem', md: '2.25rem' },
                  lineHeight: 1.2,
                  letterSpacing: '-0.02em',
                  color: '#111827',
                  mb: 0.5
                }}
              >
                {isEditMode && questions.length > 0 ? 'Edit Survey Questions' : 'Create Survey Questions'}
              </Typography>
              <Typography
                sx={{
                  color: '#6B7280',
                  fontSize: '0.875rem',
                  fontFamily: '"Inter", sans-serif'
                }}
              >
                {isEditMode && questions.length > 0 
                  ? 'Edit existing questions or add new ones to your survey'
                  : 'Choose how you want to create your survey questions'}
              </Typography>
            </Box>

          {/* Method Selection Card - Only show if no method selected */}
          {!surveyMethod && (
            <StyledCard>
              <CardContent sx={{ p: 4 }}>
              <Typography
                sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontWeight: 600,
                    fontSize: '1.125rem',
                    color: '#111827',
                    mb: 0.5
                }}
              >
                How would you like to create your survey?
                </Typography>
                <Typography
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontSize: '0.875rem',
                    color: '#6B7280',
                    mb: 3
                  }}
                >
                  Select a method to build your customer feedback survey
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Manual Method */}
                  <MethodCard
                    selected={false}
                    onClick={() => handleMethodSelect('manual')}
                  >
                    <Radio
                      value="manual"
                      checked={false}
                      sx={{
                        color: '#D1D5DB',
                        '&.Mui-checked': {
                          color: '#4F46E5'
                        }
                      }}
                    />
                    <IconContainer bgColor="rgba(79, 70, 229, 0.1)">
                      <Edit sx={{ fontSize: 28, color: '#4F46E5' }} />
                    </IconContainer>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        sx={{
                          fontFamily: '"Inter", sans-serif',
                          fontWeight: 600,
                          fontSize: '1rem',
                          color: '#111827',
                          mb: 0.5
                        }}
                      >
                        Create Manually
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: '"Inter", sans-serif',
                          fontSize: '0.875rem',
                          color: '#6B7280',
                          lineHeight: 1.5
                        }}
                      >
                        Build your survey question by question for full control over every detail
                      </Typography>
                    </Box>
                  </MethodCard>

                  {/* AI Method */}
                  <MethodCard
                    selected={false}
                    onClick={() => handleMethodSelect('ai')}
                  >
                    <Radio
                      value="ai"
                      checked={false}
                      sx={{
                        color: '#D1D5DB',
                        '&.Mui-checked': {
                          color: '#4F46E5'
                        }
                      }}
                    />
                    <IconContainer bgColor="rgba(79, 70, 229, 0.1)">
                      <AutoAwesome sx={{ fontSize: 28, color: '#4F46E5' }} />
                    </IconContainer>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        sx={{
                          fontFamily: '"Inter", sans-serif',
                          fontWeight: 600,
                          fontSize: '1rem',
                          color: '#111827',
                          mb: 0.5
                        }}
                      >
                        Generate with AI
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: '"Inter", sans-serif',
                          fontSize: '0.875rem',
                          color: '#6B7280',
                          lineHeight: 1.5
                        }}
                      >
                        Paste a product URL and let AI create relevant questions automatically
                      </Typography>
                    </Box>
                  </MethodCard>
                </Box>
              </CardContent>
            </StyledCard>
          )}

            {/* Question Builder - Show after method selection */}
            {surveyMethod && (
              <>
                {/* Step 1: Add Questions */}
                <StyledCard>
                  <Box sx={{ p: 3 }}>
                    <Typography
                      sx={{
                        fontFamily: '"Inter", sans-serif',
                        fontWeight: 600,
                        fontSize: '1.125rem',
                        color: '#111827',
                        mb: 0.5
                      }}
                    >
                      {isEditMode && questions.length > 0
                        ? 'Edit or Add Questions'
                        : surveyMethod === 'manual' 
                          ? 'Step 1: Add Questions Manually' 
                          : 'Step 1: Generate Questions with AI'}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: '"Inter", sans-serif',
                        fontSize: '0.875rem',
                        color: '#6B7280',
                        mb: 3
                      }}
                    >
                      {isEditMode && questions.length > 0
                        ? 'You can edit existing questions below or add new ones using the form above'
                        : surveyMethod === 'manual' 
                          ? 'Add your survey questions one by one' 
                          : 'Enter your product or service URL to automatically generate relevant questions'}
              </Typography>

                    {surveyMethod === 'manual' ? (
                      <ManualQuestionForm onAddQuestion={handleAddQuestion} />
                    ) : (
                      <AIQuestionGenerator
                        onGenerateQuestions={handleGenerateQuestions}
                        isGenerating={isGenerating}
                      />
                    )}
                  </Box>
                </StyledCard>

                {/* Step 2: Review & Edit Questions */}
                {questions.length > 0 && (
                  <StyledCard>
                    <Box sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Box>
                          <Typography
                            sx={{
                              fontFamily: '"Inter", sans-serif',
                              fontWeight: 600,
                              fontSize: '1.125rem',
                              color: '#111827',
                              mb: 0.5
                            }}
                          >
                            Step 2: Review & Edit Questions
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: '"Inter", sans-serif',
                              fontSize: '0.875rem',
                              color: '#6B7280'
                            }}
                          >
                            Drag to reorder, click edit to modify, or delete unwanted questions
                          </Typography>
                        </Box>
                        <Chip
                          label={`${questions.length} Questions`}
                          sx={{
                            fontFamily: '"Inter", sans-serif',
                            backgroundColor: '#EEF2FF',
                            color: '#4F46E5',
                            fontWeight: 600,
                            fontSize: '0.875rem'
                          }}
                        />
                      </Box>
                      
                      <QuestionList
                        questions={questions}
                        onEditQuestion={handleEditQuestion}
                        onDeleteQuestion={handleDeleteQuestion}
                        onReorderQuestions={handleReorderQuestions}
                      />
                    </Box>
                  </StyledCard>
                )}
              </>
            )}
          </Box>

          {/* Right Column - Preview Panel */}
          <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
            <Box sx={{ position: 'sticky', top: 24 }}>
              <StyledCard>
                <Box sx={{ p: 3 }}>
                  <Typography
                    sx={{
                      fontFamily: '"Inter", sans-serif',
                      fontWeight: 700,
                      fontSize: '1rem',
                      color: '#111827',
                      mb: 0.5
                    }}
                  >
                    Survey Preview
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: '"Inter", sans-serif',
                      fontSize: '0.875rem',
                      color: '#6B7280',
                      mb: 3
                    }}
                  >
                    {questions.length === 0 
                      ? 'Preview will appear here' 
                      : `${questions.length} question${questions.length !== 1 ? 's' : ''}`}
                  </Typography>

                  {questions.length === 0 ? (
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      py: 8,
                      px: 3,
                      textAlign: 'center',
                      backgroundColor: '#F9FAFB',
                      borderRadius: '8px'
                    }}>
                      <Box sx={{ 
                        width: 56, 
                        height: 56, 
                        borderRadius: '50%', 
                        backgroundColor: '#E5E7EB',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2
                      }}>
                        <Typography sx={{ fontSize: '1.5rem' }}>📋</Typography>
                      </Box>
                      <Typography
                        sx={{
                          fontFamily: '"Inter", sans-serif',
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          color: '#111827',
                          mb: 0.5
                        }}
                      >
                        No questions yet
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: '"Inter", sans-serif',
                          fontSize: '0.75rem',
                          color: '#6B7280'
                        }}
                      >
                        Add questions to see preview
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      <Box sx={{ 
                        backgroundColor: '#F9FAFB', 
                        border: '1px solid #E5E7EB', 
                        borderRadius: '8px', 
                        p: 2.5,
                        mb: 3,
                        maxHeight: 'calc(100vh - 350px)',
                        overflowY: 'auto'
                      }}>
                        <SurveyPreviewQuestions questions={questions} />
                      </Box>

                      <Button
                        fullWidth
                        variant="contained"
                        size="large"
                        onClick={handleSaveAndContinue}
                        disabled={questions.length === 0 || isLoading}
                        sx={{
                          fontFamily: '"Inter", sans-serif',
                          height: 48,
                          backgroundColor: '#4F46E5',
                          fontWeight: 600,
                          fontSize: '1rem',
                          textTransform: 'none',
                          borderRadius: '8px',
                          boxShadow: 'none',
                          '&:hover': {
                            backgroundColor: '#4338CA',
                            boxShadow: 'none'
                          },
                          '&.Mui-disabled': {
                            backgroundColor: '#E5E7EB',
                            color: '#9CA3AF'
                          }
                        }}
                      >
                        {isLoading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Save & Continue'}
                      </Button>
                    </>
                  )}
                </Box>
              </StyledCard>
            </Box>
          </Box>
        </ContentArea>
      </MainContent>

      {/* Success/Error Notifications */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* Edit Question Dialog */}
      <Dialog 
        open={!!editingQuestion} 
        onClose={handleCancelEdit}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: '"Inter", sans-serif', fontWeight: 700, fontSize: '1.125rem' }}>
          Edit Question
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2 }}>
            <TextField
              label="Question Text"
              fullWidth
              multiline
              rows={3}
              value={editForm.question}
              onChange={(e) => setEditForm({ ...editForm, question: e.target.value })}
              placeholder="Enter your question here..."
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '0.875rem'
                },
                '& .MuiInputLabel-root': {
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '0.875rem'
                }
              }}
            />

            <FormControl fullWidth>
              <InputLabel sx={{ fontFamily: '"Inter", sans-serif', fontSize: '0.875rem' }}>
                Answer Type
              </InputLabel>
              <Select
                value={editForm.answerType}
                label="Answer Type"
                onChange={(e) => setEditForm({ ...editForm, answerType: e.target.value })}
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '0.875rem'
                }}
              >
                <MenuItem value="text" sx={{ fontFamily: '"Inter", sans-serif', fontSize: '0.875rem' }}>Text</MenuItem>
                <MenuItem value="rating" sx={{ fontFamily: '"Inter", sans-serif', fontSize: '0.875rem' }}>Rating (1-5)</MenuItem>
                <MenuItem value="number" sx={{ fontFamily: '"Inter", sans-serif', fontSize: '0.875rem' }}>Number</MenuItem>
                <MenuItem value="yesno" sx={{ fontFamily: '"Inter", sans-serif', fontSize: '0.875rem' }}>Yes/No</MenuItem>
                <MenuItem value="multiple_choice" sx={{ fontFamily: '"Inter", sans-serif', fontSize: '0.875rem' }}>Multiple Choice</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Checkbox
                  checked={editForm.isMandatory}
                  onChange={(e) => setEditForm({ ...editForm, isMandatory: e.target.checked })}
                />
              }
              label="Make this question mandatory"
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '0.875rem'
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button 
            onClick={handleCancelEdit}
            sx={{
              fontFamily: '"Inter", sans-serif',
              color: '#6B7280',
              fontWeight: 600,
              fontSize: '0.875rem',
              textTransform: 'none',
              px: 2,
              '&:hover': {
                backgroundColor: '#F3F4F6'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveEdit} 
            variant="contained"
            disabled={!editForm.question.trim()}
            sx={{
              fontFamily: '"Inter", sans-serif',
              backgroundColor: '#4F46E5',
              fontWeight: 600,
              fontSize: '0.875rem',
              textTransform: 'none',
              px: 2,
              borderRadius: '8px',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#4338CA',
                boxShadow: 'none'
              },
              '&.Mui-disabled': {
                backgroundColor: '#E5E7EB',
                color: '#9CA3AF'
              }
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default CreateSurvey;

